import { autocompletion, CompletionContext, CompletionResult } from "@codemirror/autocomplete";
import { Decoration, DecorationSet, EditorView, keymap, ViewPlugin, ViewUpdate, WidgetType } from "@codemirror/view";
import { StateEffect, StateField, RangeSetBuilder } from "@codemirror/state";
import type { RefObject } from "react";
import type { FetchParam } from "@store/reducers/userDefinedFlags.reducer";
import { BackTestResult } from "./types";
import { isFormulaCompatibleWellnessMetric } from "@utils/wellness_utils";

// ─── Existing utils ───────────────────────────────────────────────────────────

// Identifier-based keywords in the condition language — not operands
const CONDITION_KEYWORDS = new Set(["and", "or", "var", "max", "min"]);
const OP_NUM_RE = /^op\d+$/;

export const extractOperands = (condition: string): string[] => {
    const matches = condition.match(/\bop\d+\b/g) || [];
    return [...new Set(matches)].sort(
        (a, b) => parseInt(a.replace("op", "")) - parseInt(b.replace("op", ""))
    );
};

export const splitMetricIdentifier = (identifier: string): { base: string; stat: string } => {
    if (!identifier) return { base: "", stat: "avg" };
    const parts = identifier.split("-");
    if (parts.length < 3) return { base: identifier, stat: "avg" };
    return { base: `${parts[0]}-${parts[1]}`, stat: parts[2] || "avg" };
};

export const buildMetricTreeData = (allMetricTypes: any) => {
    const typeLabels: Record<string, string> = {
        gps: "GPS",
        testing: "Testing",
        forceplate: "Forceplate",
        wellness: "Wellness",
        nutrition: "Nutrition",
    };
    return Object.entries(typeLabels)
        .map(([type, label]) => ({
            title: label,
            value: `__group__${type}`,
            selectable: false,
            children: (allMetricTypes[type] || [])
                .filter((metric: any) => type !== "wellness" || isFormulaCompatibleWellnessMetric(metric))
                .map((metric: any) => ({
                    title: metric.name,
                    // Prefer the backend-provided unique_identifier so user-defined metrics keep
                    // their `c` prefix (e.g. "gps-c123"); metric_type-metric_id drops it and
                    // collides with native metric ids.
                    value: metric.unique_identifier ?? `${metric.metric_type}-${metric.metric_id}`,
                })),
        }))
        .filter((group) => group.children.length > 0);
};

const fmtDate = (d: string) => {
    const [, m, day] = d.split("-");
    return `${m}/${day}`;
};

export const prepareBackTestHeatmapData = (
    result: BackTestResult,
    players: any[],
    fetchParams?: Record<string, FetchParam>,
    flagColor?: string,
) => {
    const playerIds = Object.keys(result);

    const dateSet = new Set<string>();
    playerIds.forEach((pid) => Object.keys(result[pid]).forEach((d) => dateSet.add(d)));
    const allDates = [...dateSet].sort();

    const playerMap = new Map(players.map((p: any) => [String(p.player_id), `${p.first_name} ${p.last_name}`]));

    const xCategories = allDates.map(fmtDate);
    const yCategories = playerIds.map((pid) => playerMap.get(pid) || `Player ${pid}`);

    const flaggedColor = flagColor || "#52c41a";

    const seriesData = allDates.flatMap((date, x) =>
        playerIds.map((pid, y) => {
            const entry = result[pid]?.[date];
            return {
                x,
                y,
                value: entry === undefined ? null : entry[0] ? 1 : 0,
                color: entry === undefined ? "#f0f0f0" : entry[0] ? flaggedColor : "#d9d9d9",
                custom: { ops: entry?.[1], date },
            };
        })
    );

    const tooltipFormatter = function (this: any) {
        const point = this.point;
        const { ops, date } = point.custom || {};
        const playerName = this.series.yAxis.categories[point.y];

        if (point.value === null || point.value === undefined) {
            return `<span style="font-size:11px"><b>${playerName}</b><br/>${date}: No data</span>`;
        }

        const statusHtml =
            point.value === 1
                ? `<span style="color:${flaggedColor}">● Flagged</span>`
                : `<span style="color:#999">● Not Flagged</span>`;

        const opsHtml = ops
            ? `<br/>${Object.entries(ops)
                  .map(([k, v]) => `${fetchParams?.[k]?.label ?? k}: ${v}`)
                  .join("<br/>")}`
            : "";

        return `<span style="font-size:11px"><b>${playerName}</b><br/>${date}<br/>${statusHtml}${opsHtml}</span>`;
    };

    return { xCategories, yCategories, seriesData, tooltipFormatter };
};

// ─── Operand label decorations ────────────────────────────────────────────────

export const startEditEffect = StateEffect.define<{ operandName: string; from: number; to: number }>();
export const confirmEditEffect = StateEffect.define<{ operandName: string; newLabel: string }>();
export const cancelEditEffect = StateEffect.define<null>();

export const editingOperandField = StateField.define<{ operandName: string; from: number; to: number } | null>({
    create: () => null,
    update: (value, tr) => {
        for (const e of tr.effects) {
            if (e.is(startEditEffect)) return e.value;
            if (e.is(confirmEditEffect) || e.is(cancelEditEffect)) return null;
        }
        return value;
    },
});

const PENCIL_SVG = `<svg viewBox="0 0 12 12" width="10" height="10" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M7.5 1.5l3 3L4 11H1V8L7.5 1.5z"/></svg>`;
const CHECK_SVG = `<svg viewBox="0 0 12 12" width="10" height="10" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="1 6 4.5 9.5 11 2"/></svg>`;
const CLOSE_SVG = `<svg viewBox="0 0 12 12" width="10" height="10" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><line x1="2" y1="2" x2="10" y2="10"/><line x1="10" y1="2" x2="2" y2="10"/></svg>`;

class OperandLabelWidget extends WidgetType {
    constructor(
        private operandName: string,
        private label: string,
        private isEditing: boolean,
        private onEdit: () => void,
        private onConfirm: (newLabel: string) => void,
        private onCancel: () => void,
        private onDelete: () => void,
    ) {
        super();
    }

    eq(other: OperandLabelWidget): boolean {
        return (
            other.operandName === this.operandName &&
            other.label === this.label &&
            other.isEditing === this.isEditing
        );
    }

    toDOM(): HTMLElement {
        const wrap = document.createElement("span");
        wrap.className = "cm-operand-tag";
        wrap.setAttribute("data-operand", this.operandName);

        if (this.isEditing) {
            const input = document.createElement("input");
            input.className = "cm-operand-edit-input";
            input.value = this.label;
            input.size = Math.max(this.label.length + 2, 6);

            const handleConfirm = () => this.onConfirm(input.value.trim() || this.label);

            input.addEventListener("keydown", (e) => {
                if (e.key === "Enter") { handleConfirm(); e.preventDefault(); }
                if (e.key === "Escape") { this.onCancel(); e.preventDefault(); }
                e.stopPropagation();
            });
            input.addEventListener("mousedown", (e) => e.stopPropagation());
            input.addEventListener("click", (e) => e.stopPropagation());

            const checkBtn = document.createElement("button");
            checkBtn.type = "button";
            checkBtn.className = "cm-operand-check-btn";
            checkBtn.innerHTML = CHECK_SVG;
            checkBtn.title = "Confirm";
            checkBtn.addEventListener("mousedown", (e) => { e.preventDefault(); e.stopPropagation(); handleConfirm(); });

            const cancelBtn = document.createElement("button");
            cancelBtn.type = "button";
            cancelBtn.className = "cm-operand-cancel-btn";
            cancelBtn.innerHTML = CLOSE_SVG;
            cancelBtn.title = "Cancel";
            cancelBtn.addEventListener("mousedown", (e) => { e.preventDefault(); e.stopPropagation(); this.onCancel(); });

            wrap.appendChild(input);
            wrap.appendChild(checkBtn);
            wrap.appendChild(cancelBtn);
            setTimeout(() => { input.focus(); input.select(); }, 0);
        } else {
            const labelSpan = document.createElement("span");
            labelSpan.className = "cm-operand-label-text";
            labelSpan.textContent = this.label;

            const pencilBtn = document.createElement("button");
            pencilBtn.type = "button";
            pencilBtn.className = "cm-operand-pencil-btn";
            pencilBtn.innerHTML = PENCIL_SVG;
            pencilBtn.title = `Edit label for ${this.operandName}`;
            pencilBtn.addEventListener("mousedown", (e) => { e.preventDefault(); e.stopPropagation(); this.onEdit(); });

            const deleteBtn = document.createElement("button");
            deleteBtn.type = "button";
            deleteBtn.className = "cm-operand-delete-btn";
            deleteBtn.innerHTML = CLOSE_SVG;
            deleteBtn.title = `Remove ${this.operandName}`;
            deleteBtn.addEventListener("mousedown", (e) => { e.preventDefault(); e.stopPropagation(); this.onDelete(); });

            wrap.appendChild(labelSpan);
            wrap.appendChild(pencilBtn);
            wrap.appendChild(deleteBtn);
        }

        return wrap;
    }

    ignoreEvent() { return false; }
}

function buildDecorations(
    view: EditorView,
    fetchParams: Record<string, FetchParam>,
    onLabelConfirm: RefObject<((operandName: string, newLabel: string, from?: number, to?: number) => void) | null>,
): DecorationSet {
    const builder = new RangeSetBuilder<Decoration>();
    const editingState = view.state.field(editingOperandField, false);
    const doc = view.state.doc.toString();
    const regex = /\bop\d+\b/g;
    let match: RegExpExecArray | null;

    while ((match = regex.exec(doc)) !== null) {
        const opName = match[0];
        const label = fetchParams[opName]?.label;
        if (!label) continue;

        const chipFrom = match.index;
        const chipTo = match.index + opName.length;

        builder.add(
            chipFrom,
            chipTo,
            Decoration.replace({
                widget: new OperandLabelWidget(
                    opName,
                    label,
                    editingState?.operandName === opName && editingState?.from === chipFrom,
                    () => view.dispatch({ effects: startEditEffect.of({ operandName: opName, from: chipFrom, to: chipTo }) }),
                    (newLabel) => {
                        view.dispatch({ effects: confirmEditEffect.of({ operandName: opName, newLabel }) });
                        onLabelConfirm.current?.(opName, newLabel, chipFrom, chipTo);
                    },
                    () => view.dispatch({ effects: cancelEditEffect.of(null) }),
                    () => {
                        // Remove all occurrences of this operand from the document
                        const currentDoc = view.state.doc.toString();
                        const re = new RegExp(`\\b${opName}\\b`, "g");
                        const changes: { from: number; to: number }[] = [];
                        let m: RegExpExecArray | null;
                        while ((m = re.exec(currentDoc)) !== null) {
                            changes.push({ from: m.index, to: m.index + opName.length });
                        }
                        if (changes.length) view.dispatch({ changes });
                    },
                ),
            }),
        );
    }

    return builder.finish();
}

const operandChipKeymap = keymap.of([
    {
        key: "Backspace",
        run: (view) => {
            const sel = view.state.selection.main;
            if (!sel.empty) return false;
            const match = view.state.doc.toString().slice(0, sel.head).match(/\bop\d+$/);
            if (!match) return false;
            view.dispatch({ changes: { from: sel.head - match[0].length, to: sel.head } });
            return true;
        },
    },
    {
        key: "Delete",
        run: (view) => {
            const sel = view.state.selection.main;
            if (!sel.empty) return false;
            const match = view.state.doc.toString().slice(sel.head).match(/^op\d+\b/);
            if (!match) return false;
            view.dispatch({ changes: { from: sel.head, to: sel.head + match[0].length } });
            return true;
        },
    },
]);

export const createOperandLabelPlugin = (
    fetchParams: Record<string, FetchParam>,
    onLabelConfirm: RefObject<((operandName: string, newLabel: string) => void) | null>,
) => [
    ViewPlugin.fromClass(
        class {
            decorations: DecorationSet;
            constructor(view: EditorView) {
                this.decorations = buildDecorations(view, fetchParams, onLabelConfirm);
            }
            update(update: ViewUpdate) {
                if (
                    update.docChanged ||
                    update.viewportChanged ||
                    update.state.field(editingOperandField, false) !==
                        update.startState.field(editingOperandField, false)
                ) {
                    this.decorations = buildDecorations(update.view, fetchParams, onLabelConfirm);
                }
            }
        },
        {
            decorations: (v) => v.decorations,
            provide: (plugin) =>
                EditorView.atomicRanges.of((view) => view.plugin(plugin)?.decorations ?? Decoration.none),
        },
    ),
    operandChipKeymap,
];

// ─── Autocomplete ─────────────────────────────────────────────────────────────

// Scans `text` forward tracking open/close state for each quote type independently.
// Returns the position and character of the last unmatched opening quote, or null
// when all quotes are balanced. Handles single and double quotes without mixing.
function findOpenQuote(text: string): { quoteChar: string; start: number } | null {
    let inDouble = false;
    let inSingle = false;
    let doubleStart = -1;
    let singleStart = -1;
    for (let i = 0; i < text.length; i++) {
        const ch = text[i];
        if (ch === '"' && !inSingle) {
            if (inDouble) { inDouble = false; doubleStart = -1; }
            else { inDouble = true; doubleStart = i; }
        } else if (ch === "'" && !inDouble) {
            if (inSingle) { inSingle = false; singleStart = -1; }
            else { inSingle = true; singleStart = i; }
        }
    }
    if (inDouble) return { quoteChar: '"', start: doubleStart };
    if (inSingle) return { quoteChar: "'", start: singleStart };
    return null;
}

export type AutoCompleteCallbacks = {
    getNextOperand: () => string;
    setPendingLabel: (opName: string, label: string, quoteChar: string) => void;
    getExistingOperandForLabel: (label: string) => string | null;
};

export const createConditionAutocomplete = (
    existingOperands: string[],
    callbacksRef?: RefObject<AutoCompleteCallbacks | null>,
) => {
    const autocompleteExt = autocompletion({
        override: [
            (context: CompletionContext): CompletionResult | null => {
                if (callbacksRef) {
                    // Case 1: cursor is inside an open (unmatched) quoted string — show suggestion while typing.
                    // Uses findOpenQuote to scan the full document up to the cursor so completed earlier
                    // operands (e.g. "hr" already replaced with op1) never produce false positives.
                    const docUpToCursor = context.state.doc.sliceString(0, context.pos);
                    const openQuote = findOpenQuote(docUpToCursor);
                    if (openQuote) {
                        const label = docUpToCursor.slice(openQuote.start + 1).trim();
                        if (label) {
                            return {
                                from: openQuote.start,
                                options: [
                                    {
                                        label: `Add "${label}" as operand`,
                                        type: "text",
                                        detail: "new operand",
                                        apply: (view, _completion, from, to) => {
                                            const cb = callbacksRef.current;
                                            if (!cb) return;
                                            const existingOp = cb.getExistingOperandForLabel(label);
                                            if (existingOp) {
                                                view.dispatch({ changes: { from, to, insert: existingOp } });
                                                return;
                                            }
                                            const opName = cb.getNextOperand();
                                            cb.setPendingLabel(opName, label, openQuote.quoteChar);
                                            view.dispatch({ changes: { from, to, insert: opName } });
                                        },
                                    },
                                ],
                            };
                        }
                    }

                    // Case 2: cursor is right after a complete quoted string (both quotes present).
                    const quotedMatch = context.matchBefore(/["'][^"']*["']/);
                    if (quotedMatch) {
                        const raw = quotedMatch.text;
                        const quoteChar = raw[0];
                        if (raw.length > 2 && raw[raw.length - 1] === quoteChar) {
                            const label = raw.slice(1, -1).trim();
                            if (label) {
                                return {
                                    from: quotedMatch.from,
                                    options: [
                                        {
                                            label: `Add "${label}" as operand`,
                                            type: "text",
                                            detail: "new operand",
                                            apply: (view, _completion, from, to) => {
                                                const cb = callbacksRef.current;
                                                if (!cb) return;
                                                const existingOp = cb.getExistingOperandForLabel(label);
                                                if (existingOp) {
                                                    view.dispatch({ changes: { from, to, insert: existingOp } });
                                                    return;
                                                }
                                                const opName = cb.getNextOperand();
                                                cb.setPendingLabel(opName, label, quoteChar);
                                                view.dispatch({ changes: { from, to, insert: opName } });
                                            },
                                        },
                                    ],
                                };
                            }
                        }
                    }
                }

                // Existing op\d* autocomplete (takes priority over bare-word case below)
                const word = context.matchBefore(/op\d*/);
                if (word && (word.from !== word.to || context.explicit)) {
                    const maxNum =
                        existingOperands.length > 0
                            ? Math.max(
                                  ...existingOperands.map((op) => parseInt(op.replace("op", ""), 10)),
                              )
                            : 0;

                    const suggestions = [
                        ...existingOperands.map((op) => ({ label: op, type: "variable" as const, detail: "operand" })),
                        { label: `op${maxNum + 1}`, type: "variable" as const, detail: "new operand" },
                    ];

                    const seen = new Set<string>();
                    return {
                        from: word.from,
                        options: suggestions.filter((s) => {
                            if (seen.has(s.label)) return false;
                            seen.add(s.label);
                            return true;
                        }),
                    };
                }

                // Case 3: bare non-keyword identifier → suggest "Add as operand" (applied on click only).
                // topspeed / top_speed / ts14 are replaced with opN and stored as a pending label.
                // Requires callbacksRef (same as quoted-string cases above).
                if (callbacksRef) {
                    const bareWord = context.matchBefore(/[a-zA-Z_][a-zA-Z0-9_]*/);
                    if (bareWord && bareWord.text.length >= 2 &&
                        !CONDITION_KEYWORDS.has(bareWord.text.toLowerCase()) &&
                        !OP_NUM_RE.test(bareWord.text)
                    ) {
                        const label = bareWord.text;
                        return {
                            from: bareWord.from,
                            options: [
                                {
                                    label: `Add "${label}" as operand`,
                                    type: "text" as const,
                                    detail: "new operand",
                                    apply: (view, _completion, from, to) => {
                                        const cb = callbacksRef.current;
                                        if (!cb) return;
                                        const existingOp = cb.getExistingOperandForLabel(label);
                                        if (existingOp) {
                                            view.dispatch({ changes: { from, to, insert: existingOp } });
                                            return;
                                        }
                                        const opName = cb.getNextOperand();
                                        cb.setPendingLabel(opName, label, "");
                                        view.dispatch({ changes: { from, to, insert: opName } });
                                    },
                                },
                            ],
                        };
                    }
                }

                return null;
            },
        ],
    });

    const isInsideQuotes = (text: string, position: number): boolean => {
        let inDoubleQuote = false;
        let inSingleQuote = false;
        for (let i = 0; i < position; i++) {
            if (text[i] === '"' && (i === 0 || text[i - 1] !== "\\")) {
                inDoubleQuote = !inDoubleQuote;
            } else if (text[i] === "'" && (i === 0 || text[i - 1] !== "\\")) {
                inSingleQuote = !inSingleQuote;
            }
        }
        return inDoubleQuote || inSingleQuote;
    };

    const spaceKeymap = keymap.of([
        {
            key: "Space",
            run: (view) => {
                if (!callbacksRef?.current) return false;
                const sel = view.state.selection.main;
                if (!sel.empty) return false;
                const before = view.state.doc.sliceString(0, sel.head);

                if (isInsideQuotes(before, sel.head)) {
                    return false;
                }

                const match = before.match(/[a-zA-Z_][a-zA-Z0-9_]*$/);
                if (!match) return false;
                const word = match[0];
                if (CONDITION_KEYWORDS.has(word.toLowerCase()) || OP_NUM_RE.test(word)) return false;
                const cb = callbacksRef.current;
                const from = sel.head - word.length;
                const existingOp = cb.getExistingOperandForLabel(word);
                if (existingOp) {
                    view.dispatch({ changes: { from, to: sel.head, insert: existingOp + " " } });
                } else {
                    const opName = cb.getNextOperand();
                    cb.setPendingLabel(opName, word, "");
                    view.dispatch({ changes: { from, to: sel.head, insert: opName + " " } });
                }
                return true;
            },
        },
    ]);

    return [autocompleteExt, spaceKeymap];
};
